import type { ErrorResult, Result, SuccessResult } from "./types";
/**
 * Creates a new error result with the specified error details
 * @param code - Error code (e.g., "NOT_FOUND", 404)
 * @param message - Human-readable error message
 * @param detail - Technical error details for developers
 * @returns An ErrorResult object
 */
export function newErrorResult(
  code: string | number = "UNKNOWN_ERROR",
  message = "An unexpected error has occurred",
  detail = "An unknown error occurred"
): ErrorResult {
  return {
    code,
    detail,
    message,
    isOk: false,
    isError: true,
  };
}

/**
 * Creates a new success result with the specified data and details
 * @param data - The successful operation result data
 * @param code - Success code (e.g., "SUCCESS", 200)
 * @param message - Human-readable success message
 * @param detail - Technical details about the operation
 * @returns A SuccessResult object
 */
export function newSuccessResult<T>(
  data: T,
  code: string | number = "SUCCESS",
  message = "Operation completed successfully",
  detail = "Operation completed successfully"
): SuccessResult<T> {
  return {
    code,
    detail,
    message,
    data,
    isOk: true,
    isError: false,
  };
}

/**
 * Checks if a result is a success result
 * @param result - The result to check
 * @returns true if the result is a success result
 */
export function isSuccess<T>(result: Result<T>): result is SuccessResult<T> {
  return result.isOk;
}

/**
 * Checks if a result is an error result
 * @param result - The result to check
 * @returns true if the result is an error result
 */
export function isError<T>(result: Result<T>): result is ErrorResult {
  return result.isError;
}
